import { useCallback } from 'react';
import { createResource, useResource, type RouteResource } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
// eslint-disable-next-line jira/import-whitelist
import { userPreferenceAPI } from '@jira/platform__services-user-preferences/src/utils/index.tsx';
import type {
	GICSmartFieldsFeatureResourceData,
	GICSmartFieldsMyPreferencesResponse,
} from './GICSmartFieldsFeatureResourceData.types.tsx';
import { STORE_KEY, RESOURCE_KEY, PACKAGE_NAME } from './resourceType.constants.tsx';

export const gicSmartFieldsFeatureResource: RouteResource<GICSmartFieldsFeatureResourceData> =
	createResource<GICSmartFieldsFeatureResourceData>({
		type: 'RESOURCE_TYPE_GIC_SMART_FIELDS',
		getKey: () => RESOURCE_KEY,
		getData: async (): Promise<GICSmartFieldsFeatureResourceData> => {
			try {
				const data = await userPreferenceAPI.get<GICSmartFieldsMyPreferencesResponse>(STORE_KEY);
				if (!data)
					return {
						hasUserOptedOut: false,
						hasUserDismissedReadMore: false,
						hasUserDismissedGicOnboarding: false,
					};
				return data;
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				// If value has never been set, we expect a 404
				if (error.statusCode === 404) {
					return {
						hasUserOptedOut: false,
						hasUserDismissedReadMore: false,
						hasUserDismissedGicOnboarding: false,
					};
				}
				fireErrorAnalytics({
					error,
					meta: {
						id: 'gicSmartFieldsFeatureResource',
						packageName: PACKAGE_NAME,
						teamName: 'gryffindor',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
			return {
				hasUserOptedOut: false,
				hasUserDismissedReadMore: false,
				hasUserDismissedGicOnboarding: false,
			};
		},
		maxAge: Number.MAX_VALUE,
		isPageShellCritical: true,
	});

export const useIsSmartFieldsOptIn = (): {
	isSmartFieldsOptIn: boolean | null;
	isSmartFieldDismissedReadMore: boolean | null;
	isSmartFieldsGicOnboardingDismissed: boolean | null;
	dismissReadMore: () => void;
	dismissGicOnboarding: () => void;
	loading: boolean;
} => {
	const { data, update, loading } = useResource(gicSmartFieldsFeatureResource);

	const currentOptInStatus =
		typeof data?.hasUserOptedOut === 'boolean' ? !data.hasUserOptedOut : true;

	const currentDismissedReadMore =
		typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false;

	const currentDismissedGicOnboarding =
		typeof data?.hasUserDismissedGicOnboarding === 'boolean'
			? data.hasUserDismissedGicOnboarding
			: false;

	const dismissReadMore = useCallback((): void => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			hasUserDismissedReadMore: true,
			...(previousData?.hasUserDismissedGicOnboarding
				? { hasUserDismissedGicOnboarding: previousData.hasUserDismissedGicOnboarding }
				: { hasUserDismissedGicOnboarding: false }),
		}));
	}, [update]);

	const dismissGicOnboarding = useCallback((): void => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			...(previousData?.hasUserDismissedReadMore
				? { hasUserDismissedReadMore: previousData.hasUserDismissedReadMore }
				: { hasUserDismissedReadMore: false }),
			hasUserDismissedGicOnboarding: true,
		}));
	}, [update]);

	return {
		isSmartFieldsOptIn: loading ? null : currentOptInStatus,
		isSmartFieldDismissedReadMore: loading ? null : currentDismissedReadMore,
		isSmartFieldsGicOnboardingDismissed: loading ? null : currentDismissedGicOnboarding,
		dismissReadMore,
		dismissGicOnboarding,
		loading,
	};
};
